import { z } from "zod";
import { http } from "../api";
import { http as http_v2 } from "../api_v2";
import {
  NewPlanModelV1,
  NewPlanModelV2,
  PlanModelSchemaV1,
  PlanModelSchemaV2,
  UpdatePlanModel,
  UpdatePlanV2,
} from "./types";

/**
 * Returns the details of a plan for a given plan id
 *
 * @param planId The id of the plan to fetch details for
 * @returns A list of detailed data associated with a plan
 */
async function getPlanForPlanIdV1(planId: number) {
  const planResponse = await http.get(`/v1/plans/${planId}`);
  return PlanModelSchemaV1.parse(planResponse.data);
}

/**
 * Returns the details of a plan for a given plan id
 *
 * @param planId The id of the plan to fetch details for
 * @returns A list of detailed data associated with a plan
 */
async function getPlanForPlanIdV2(planId: number) {
  const planResponse = await http_v2.get(`plans/${planId}`);
  return PlanModelSchemaV2.parse(planResponse.data);
}

/**
 * Updates a plan to the model provided. Only updates a subset of the plan data.
 *
 * @param updatePlanModel The data to be updated on the back end.
 */
async function updatePlanV1(updatePlanModel: UpdatePlanModel) {
  await http.put(`/v1/plans/${updatePlanModel.id}`, {
    planModel: updatePlanModel,
  });
}

/**
 * Updates a plan to the model provided. Only updates a subset of the plan data.
 *
 * @param updatePlanModel The data to be updated on the back end.
 */
async function updatePlanV2(updatePlanModel: UpdatePlanV2) {
  await http_v2.put(`/plans/${updatePlanModel.id}`, updatePlanModel);
}

/**
 * Creates a new plan using the model provided. Returns the plan id.
 *
 * @param newPlanModel A subset of plan fields needed to create a new plan.
 * @returns The id of the new plan.
 */
async function createPlanV1(newPlanModel: NewPlanModelV1) {
  const response = await http.post(`/v1/plans/newplan`, newPlanModel);

  // The plan id is returned as a url within the headers. This just splits
  // it out to get the actual id.
  const url = URL.parse(response.headers.location);
  const planId = url?.pathname.split("/").at(-1);
  return z.coerce.number().parse(planId);
}

/**
 * Creates a new plan using the model provided. Returns the plan id.
 *
 * @param newPlanModel A subset of plan fields needed to create a new plan.
 * @returns The id of the new plan.
 */
async function createPlanV2(newPlanModel: NewPlanModelV2) {
  const response = await http_v2.post(`/plans`, newPlanModel);

  // The plan id is returned as a url within the headers. This just splits
  // it out to get the actual id.
  const url = URL.parse(response.headers.location);
  const planId = url?.pathname.split("/").at(-1);
  return z.coerce.number().parse(planId);
}

/**
 * Abandons the plan specified by plan id
 *
 * @param planId The plan id of the plan to be abandoned.
 */
async function abandonPlan(planId: number) {
  await http_v2.post(`/plans/${planId}/abandon`);
}

/**
 * Reactivates a disabled plan.
 *
 * @param planId The plan id of the plan to be reactivated.
 */
async function reactivatePlan(planId: number) {
  await http_v2.post(`/plans/${planId}/reactivate`);
}

export default {
  getPlanForPlanIdV1,
  getPlanForPlanIdV2,
  updatePlanV1,
  updatePlanV2,
  createPlanV1,
  createPlanV2,
  abandonPlan,
  reactivatePlan,
};
